<template>
  <div class="eraser-tools" :class="{ mobile: mobile }">
    <div v-if="!mobile" class="eraser-buttons" role="group" :aria-label="$t('editor.brushTools')">
    <button
      v-for="tool in ['erase', 'restore']"
      :key="tool"
      type="button"
      class="eraser-toggle"
      :class="{ active: enabled && mode === tool }"
      :disabled="disabled"
      :aria-pressed="enabled && mode === tool"
      @click="selectTool(tool)"
    >
      <svg
        class="eraser-icon"
        width="20"
        height="20"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <template v-if="tool === 'erase'">
          <path d="M7.2 20.8 18.8 7.4a2 2 0 0 1 2.9-.2l5.1 4.5a2 2 0 0 1 .2 2.9L16.4 26H11l-3.7-3.2a1.5 1.5 0 0 1-.1-2z" />
          <path d="m14.4 12.7 8.1 7.1M11 26h15" />
        </template>
        <g v-else transform="rotate(-45 16 16)">
          <rect x="3" y="10" width="26" height="12" rx="4" />
          <path d="M11 10v12M21 10v12" />
          <path d="M15 14h.01M18 14h.01M15 18h.01M18 18h.01M7 14h.01M7 18h.01M25 14h.01M25 18h.01" stroke-width="2.5" />
        </g>
      </svg>
      <span>{{ $t(tool === 'erase' ? 'editor.eraserTools' : 'editor.repairBrush') }}</span>
    </button>
    </div>

    <template v-if="enabled">
      <label class="eraser-size-control">
        <span>{{ $t('editor.eraserSize') }}</span>
        <div class="slider-control">
          <input
            type="range"
            class="eraser-slider"
            min="1"
            max="80"
            step="1"
            :value="size"
            :disabled="disabled"
            :style="sliderStyle"
            :aria-label="$t('editor.eraserSize')"
            @input="handleSliderInput"
          />
          <div class="value-input-wrap">
            <input
              type="number"
              class="value-input"
              min="1"
              max="80"
              step="1"
              :value="size"
              :disabled="disabled"
              :aria-label="$t('editor.eraserSize')"
              @input="handleNumberInput"
              @blur="handleNumberBlur"
            />
            <span class="value-suffix">px</span>
          </div>
        </div>
      </label>

    </template>
  </div>
</template>

<script>
export default {
  name: 'EraserTools',
  props: {
    enabled: { type: Boolean, default: false },
    size: { type: Number, default: 40 },
    mode: { type: String, default: 'erase' },
    disabled: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false }
  },
  emits: ['toggle', 'update-size', 'update-mode'],
  computed: {
    sliderStyle() {
      const percent = ((this.size - 1) / (80 - 1)) * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 0%, #4A90D9 ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`
      };
    }
  },
  methods: {
    selectTool(mode) {
      if (this.disabled) return;
      if (this.enabled && this.mode === mode) {
        this.$emit('toggle', false);
      } else {
        this.$emit('update-mode', mode);
      }
    },
    clampSize(value) {
      return Math.max(1, Math.min(80, value));
    },
    handleSliderInput(event) {
      this.$emit('update-size', this.clampSize(Number(event.target.value)));
    },
    handleNumberInput(event) {
      const value = Number.parseInt(event.target.value, 10);
      // Keep empty or out-of-range input editable until blur.
      if (!Number.isFinite(value) || value < 1 || value > 80) return;
      this.$emit('update-size', value);
    },
    handleNumberBlur(event) {
      const value = Number.parseInt(event.target.value, 10);
      const size = this.clampSize(Number.isFinite(value) ? value : this.size);
      event.target.value = String(size);
      this.$emit('update-size', size);
    }
  }
};
</script>

<style scoped>
.eraser-tools {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.eraser-buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.eraser-toggle {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  min-height: 52px;
  padding: 8px 0;
  border: 1px solid #d7dee6;
  border-radius: 7px;
  background: #fff;
  color: #26374e;
  font: inherit;
  font-size: 8px;
  cursor: pointer;
}

.eraser-toggle span {
  max-width: 100%;
  white-space: nowrap;
  letter-spacing: -0.15px;
  line-height: 1.2;
}

.eraser-icon {
  flex: 0 0 auto;
}

.eraser-toggle.active {
  border-color: #55768f;
  background: #e8f0fc;
}

.eraser-toggle:focus-visible {
  outline: 2px solid #55768f;
  outline-offset: 2px;
}

.eraser-toggle:disabled,
.eraser-slider:disabled,
.value-input:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.eraser-size-control {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #374151;
  font-size: 12px;
}

.slider-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.eraser-slider {
  width: 90px;
  flex: 1 1 0%;
  min-width: 0;
  height: 6px;
  border-radius: 3px;
  appearance: none;
  cursor: pointer;
}

.eraser-slider::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #4A90D9;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.eraser-slider::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #4A90D9;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.value-input-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.value-input {
  width: 40px;
  padding: 4px 6px;
  font: inherit;
  font-size: 12px;
  text-align: center;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  -moz-appearance: textfield;
}

.value-input::-webkit-inner-spin-button,
.value-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.value-input:focus {
  outline: none;
  border-color: #858d95;
}

.value-suffix {
  font-size: 12px;
  color: #6b7280;
}

.eraser-tools.mobile {
  width: 100%;
}

.eraser-tools.mobile .slider-control {
  width: 100%;
}

.eraser-tools.mobile .eraser-slider {
  flex: 1 1 0%;
  min-width: 0;
  width: auto;
}
</style>